import Loader, { Status } from '@/components/Loader/Loader';
import DatePickerScheduledSend from '@/components/DatePickerScheduledSend/DatePickerScheduledSend';
import BusinessPreviewEmailModal from '@/modules/dashboard/components/BusinessPreviewEmailModal/BusinessPreviewEmailModal';
import type { Business, List } from '@/modules/dashboard/dashboardTypes';
import { dashboardSelectBusiness } from '@/modules/dashboard/dashboardActions';
import {
	GoldCalendar,
	EnvelopeRounded,
	ChatGPT,
	EmailSecondary,
	GoogleButtonIcon,
	MsButtonIcon
} from '@/assets/images';
import QuestionMarkPNG from '@/assets/images/question-mark.png';
import type { RootState } from '@/rootReducer';
import validateEmail from '@/utils/emailValidation';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { Resizable } from 're-resizable';
import React, { useEffect, useState, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { uid } from 'react-uid';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { sendAnalytics } from '@/services/analyticsService';
import { startNylasUser } from '@/modules/emailOnboarding/emailOnboardingActions';
import { normalizeHTML } from '@/utils/normalizeHTML';
import { Pagination } from '@/components/atoms/Pagination';
import { usePagination } from '@/hooks/usePagination';
import { UserRole } from '@/modules/user/userTypes';
import type { FilterProps } from '@/components/molecules/Filters';

import {
	emailClearAIRegenerate,
	emailSendEmailsRequest
} from '../../emailActions';
import type {
	DirectEmail,
	Email,
	EmailSuggestion,
	EmailTemplate
} from '../../emailTypes';
import EmailForm from '../EmailForm/EmailForm';
import './EmailList.scss';
import EmailListItem from './EmailListItem/EmailListItem';

type Props = FilterProps & {
	data: Business[];
	emailData: Email;
	activeBusiness: Business | undefined;
	onHide: (id: string) => void;
	onSelect: (business: Business) => void;
	directEmail?: DirectEmail;
	user: any;
	selectedTemplate: EmailTemplate;
	setSelectedTemplate: (template: EmailTemplate) => void;
	setDeleteDataIndex?: (x: number) => void;
	setShowAiLoadingModal: (x: boolean) => void;
	rotateTips: () => void;
};

export default function EmailList({
	data,
	onHide,
	onSelect,
	activeBusiness,
	emailData,
	directEmail,
	user,
	selectedTemplate,
	setSelectedTemplate,
	setDeleteDataIndex,
	setShowAiLoadingModal,
	rotateTips,
	appliedFilters,
	onFilterChange
}: Props): JSX.Element {
	const [
		emailSent,
		emailIsLoading,
		emailSuccess,
		emailFailure,
		replyMessageId,
		listSelected,
		businessesCount,
		businessesIsLoading,
		businesses,
		selectedBusiness,
		emailSubjectForReply,
		scheduledEmailActivity,
		scheduledEmailLimits,
		cancelScheduledEmailSuccess,
		staleTemplates,
		aiText,
		aiLoading,
		aiCredits,
		aiRegenerateSuccessId
	] = useSelector<
		RootState,
		[
			any,
			boolean,
			boolean,
			boolean,
			string,
			List,
			number,
			boolean,
			any,
			any,
			any,
			any,
			boolean,
			any,
			any,
			any,
			boolean,
			number,
			boolean
		]
	>(
		(state: RootState) => [
			state.email.getIn(['email', 'sent']).toJS(),
			state.email.getIn(['email', 'isLoading']),
			state.email.getIn(['email', 'success']),
			state.email.getIn(['email', 'failure']),
			state.email.getIn(['email', 'replyMessageId']),
			state.dashboard.getIn(['lists', 'selected']).toJS(),
			state.dashboard.getIn(['businesses', 'count']),
			state.dashboard.getIn(['businesses', 'isLoading']),
			state.dashboard.getIn(['businesses', 'data']).toJS(),
			state.dashboard.getIn(['businesses', 'selected']),
			state.email.getIn(['email', 'replySubject']),
			state.email.getIn(['email', 'scheduled', 'activity']).toJS(),
			state.email.getIn(['email', 'scheduled', 'limits']).toJS(),
			state.email.getIn(['email', 'scheduled', 'cancel', 'success']),
			state.email.getIn(['templates', 'stale']).toJS(),
			state.email.getIn(['ai', 'text']).toJS(),
			state.email.getIn(['ai', 'loading']),
			state.email.getIn(['ai', 'credits']),
			state.email.getIn(['ai', 'regenerate', 'successId'])
		],
		shallowEqual
	);

	const [email, setEmail] = useState<string | undefined>('');
	const [bcc, setBcc] = useState<string | undefined>('');
	const [subject, setSubject] = useState<string>('');
	const [body, setBody] = useState<string>('');
	const [editedEmails, setEditedEmails] = useState<{ [x: string]: Email }>(
		{}
	);
	const [businessEmails, setBusinessEmails] = useState<EmailSuggestion[]>([]);
	const [emailStatus, setEmailStatus] = useState<number>(Status.NONE);
	const [templateWarningOpen, setTemplateWarningOpen] = useState<boolean>();
	const [to, setTo] = useState<string>(
		String((directEmail?.email || email) ?? '').toLowerCase()
	);

	const dispatch = useDispatch();
	const sendEmails = (emails: Email[]) =>
		dispatch(emailSendEmailsRequest({ emails }));
	const startEmailSignup = payload => dispatch(startNylasUser(payload));

	const selectBusiness = (b: Partial<Business>) =>
		dispatch(dashboardSelectBusiness(b as Business));
	const clearAIRegenerate = () => dispatch(emailClearAIRegenerate());

	const replaceVariables = (value: string): string =>
		value
			.replaceAll(/\[business_name\]/gi, activeBusiness?.name ?? '')
			.replaceAll(/\[list_name\]/gi, listSelected?.name ?? '');

	const removeAiTags = (value: string): string =>
		value.replaceAll(/\[\/?AI\]/g, '');

	const [emailReady, setEmailReady] = useState<{
		email?: boolean;
		bcc?: boolean;
		subject?: boolean;
		body?: boolean;
	}>({ email: true, bcc: true, subject: true, body: true });

	const setPrimary = businessEmails =>
		businessEmails[0] ? (businessEmails[0].isPrimary = true) : null;

	const [nylasSynced, setNylasSyncStatus] = useState(
		user?.nylasSyncStatus == 'running' ||
			user?.nylasSyncStatus == 'nothing' ||
			user?.nylasSyncStatus == undefined ||
			user?.nylasSyncStatus == 'partial'
	);

	const closeReauthenticate = () => {
		setNylasSyncStatus(true);
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `User closed Nylas sync warning`
			});
		}
	};

	const [selectedDateAndTime, setSelectedDateAndTime] =
		useState<Dayjs | null>(
			scheduledEmailActivity[`${activeBusiness?._id}`]
				? dayjs(
						scheduledEmailActivity[`${activeBusiness?._id}`]
							.requestedScheduleDate,
						'MM/DD/YYYY, h:mm:ss A'
					)
				: null
		);
	const scheduledEmailDate = selectedDateAndTime?.format('MM-DD-YYYY');
	const scheduledEmailDateAndTime =
		selectedDateAndTime?.format('MM-DD-YYYY-HH-mm');
	const prevTimeSlot = selectedDateAndTime
		?.subtract(30, 'minute')
		.format('MM-DD-YYYY-HH-mm');

	const [showEmailLimitWarning, setShowEmailLimitWarning] =
		useState<boolean>(false);

	useEffect(() => {
		if (scheduledEmailActivity[`${activeBusiness?._id}`]) {
			setSelectedDateAndTime(
				dayjs(
					scheduledEmailActivity[`${activeBusiness?._id}`]
						.requestedScheduleDate,
					'MM/DD/YYYY, h:mm:ss A'
				)
			);

			if (showEmailLimitWarning) {
				setShowEmailLimitWarning(false);
			}

			return;
		}

		if (
			scheduledEmailLimits[scheduledEmailDate]?.units[
				scheduledEmailDateAndTime
			]?.limit === 0
		) {
			setSelectedDateAndTime(selectedDateAndTime?.add(30, 'minute'));
			setShowEmailLimitWarning(true);
		} else if (showEmailLimitWarning) {
			setShowEmailLimitWarning(false);
		}
	}, [activeBusiness]);

	useEffect(() => {
		if (cancelScheduledEmailSuccess) {
			setSelectedDateAndTime(null);
		}
	}, [cancelScheduledEmailSuccess]);

	const isSendDisabled =
		emailSent[activeBusiness?._id ?? 0] || emailIsLoading;

	const disabledClassCheck = () => {
		return (
			isSendDisabled ||
			!email ||
			(email && !emailReady?.email) ||
			(!!bcc && !emailReady?.bcc) ||
			(!subject && !emailReady?.subject) ||
			(!body.replace(/<[^>]*>?/gm, '') && !emailReady?.body) ||
			scheduledEmailActivity[`${activeBusiness?._id}`] ||
			scheduledEmailLimits[scheduledEmailDate]?.limit === 0 ||
			staleTemplates?.[`${selectedTemplate?.id}`] ||
			(user.emailLimitLeft === 0 &&
				user.role !== UserRole.PreTrial &&
				!selectedDateAndTime) ||
			nylasSynced !== true ||
			((aiLoading || aiCredits <= 0) &&
				!aiText?.[`${listSelected.id}`]?.[`${selectedTemplate?.id}`]?.[
					`${activeBusiness?._id}`
				] &&
				selectedTemplate?.body?.includes('[ai_snippet]'))
		);
	};

	useEffect(() => {
		if (
			emailIsLoading &&
			!emailSuccess &&
			!emailFailure &&
			String(emailFailure).trim() !== 'Error: Email limit reached'
		) {
			setEmailStatus(Status.LOADING);
		} else if (
			!emailIsLoading &&
			!emailSuccess &&
			!emailSent[activeBusiness?._id ?? 0] &&
			emailFailure &&
			emailStatus !== 3 &&
			emailStatus !== 0
		) {
			setEmailStatus(Status.FAILURE);
		} else if (
			(emailSent[activeBusiness?._id ?? 0] ||
				emailSent[activeBusiness?.id ?? 0] ||
				emailSent[activeBusiness?.businessId ?? 0]) &&
			emailIsLoading !== undefined &&
			!emailIsLoading &&
			emailSuccess
		) {
			setEmailStatus(Status.SUCCESS);
		} else if (
			emailSent[activeBusiness?._id ?? 0] &&
			emailIsLoading !== undefined &&
			emailIsLoading &&
			String(emailFailure).trim() === 'Error: Email limit reached'
		) {
			setEmailStatus(Status.FAILURE);
		} else {
			setEmailStatus(Status.NONE);
		}
	}, [activeBusiness, emailIsLoading, emailSuccess, emailFailure]);

	useEffect(() => {
		if (
			emailFailure &&
			String(emailFailure).trim() ===
				'Could not verify access credential.'
		) {
			setNylasSyncStatus(false);
		}
	}, [activeBusiness, emailFailure]);

	const checkEmailReady = () => {
		let newEmailReady: {
			email?: boolean;
			subject?: boolean;
			bcc?: boolean;
			body?: boolean;
		} = {
			email: false,
			subject: false,
			bcc: true,
			body: false
		};
		if (email && email?.length > 0) {
			newEmailReady = {
				...newEmailReady,
				email: validateEmail(email)
			};
		}

		if (bcc && bcc?.length > 0) {
			newEmailReady = {
				...newEmailReady,
				bcc: validateEmail(bcc)
			};
		}

		if (subject?.length > 0) {
			newEmailReady = {
				...newEmailReady,
				subject: true
			};
		}

		if (body?.replace(/<[^>]*>?/gm, '')?.length > 0) {
			newEmailReady = {
				...newEmailReady,
				body: true
			};
		}

		setEmailReady(newEmailReady);

		return (
			newEmailReady?.body &&
			newEmailReady?.email &&
			newEmailReady?.subject &&
			newEmailReady?.bcc
		);
	};

	const sendEmail = (e: React.MouseEvent | MouseEvent) => {
		const templateCountLocal = localStorage.getItem('templateCountLocal');
		if (templateCountLocal == undefined) {
			localStorage['templateCountLocal'] = 1;
		}
		const previousTemplate = localStorage.getItem('previousTemplate');
		const currentTemplate = emailData.templateId;
		if (previousTemplate == currentTemplate) {
			localStorage['templateCountLocal'] =
				parseInt(localStorage['templateCountLocal']) + 1;
		} else {
			localStorage['templateCountLocal'] = 1;
		}
		localStorage.setItem('previousTemplate', currentTemplate);

		if (user && user.email && directEmail) {
			let sender;
			let sendingMethod;
			if (user?.threadedEmail.nylasEnabled) {
				sender = user.email;
				sendingMethod = 'Nylas';
			} else if (user?.re2Email) {
				sender = user?.re2Email;
				sendingMethod = 'Relay';
			} else {
				sender = 'Mailjet';
				sendingMethod = 'Mailjet';
			}
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'Send Email Button Clicked - Direct email',
				properties: {
					source:
						window.location.pathname == '/communication-center'
							? 'Communication Center'
							: listSelected?.id
								? 'List view'
								: 'Location View',
					businessId: activeBusiness?._id,
					businessName: activeBusiness?.name,
					scheduled: !!selectedDateAndTime,
					scheduledTime: selectedDateAndTime || undefined,
					sender,
					sendingMethod,
					AISnippet: selectedTemplate.body?.includes('[ai_snippet]')
				}
			});
		}

		if (user && user.email && !directEmail) {
			let sender;
			let sendingMethod;
			if (user?.threadedEmail.nylasEnabled) {
				sender = user.email;
				sendingMethod = 'Nylas';
			} else if (user?.re2Email) {
				sender = user?.re2Email;
				sendingMethod = 'Relay';
			} else {
				sender = 'Mailjet';
				sendingMethod = 'Mailjet';
			}
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'Send Email Button Clicked - List email',
				properties: {
					source:
						window.location.pathname == '/communication-center'
							? 'Communication Center'
							: listSelected?.id
								? 'List view'
								: 'Location View',
					listId: listSelected?.id ? listSelected?.id : 'N/A',
					businessId: activeBusiness?._id,
					businessName: activeBusiness?.name,
					scheduled: !!selectedDateAndTime,
					scheduledTime: selectedDateAndTime || undefined,
					sender,
					sendingMethod,
					AISnippet: selectedTemplate.body?.includes('[ai_snippet]')
				}
			});
		}

		const userAdded = {
			exists: false,
			index: null
		};
		if (
			activeBusiness.userAddedData &&
			activeBusiness.userAddedData.filter(d => {
				return d.data === email;
			})?.length > 0
		) {
			activeBusiness.userAddedData.forEach((dataPt, i) => {
				if (dataPt.type === 'email' && dataPt.data === email) {
					userAdded.exists = true;
					userAdded.index = i;
				}
			});
		}

		e.preventDefault();
		e.stopPropagation();

		if (activeBusiness && checkEmailReady()) {
			if (selectedDateAndTime) {
				const emailToSend = {
					subject: removeAiTags(
						replaceVariables(
							emailSubjectForReply?.subject
								? emailSubjectForReply.subject
								: subject
						)
					),
					body: normalizeHTML(removeAiTags(replaceVariables(body))),
					businessId: activeBusiness?._id || activeBusiness.id,
					business: activeBusiness,
					name: activeBusiness?.name,
					listId: listSelected.id || listSelected._id,
					email,
					bcc,
					userAdded,
					replyMessageId: replyMessageId || '',
					scheduleDate: selectedDateAndTime?.unix(),
					templateId: emailData?.templateId,
					isAiSnippetEmail:
						selectedTemplate?.body?.includes('[ai_snippet]')
				};
				if (emailToSend.bcc == '') {
					delete emailToSend.bcc;
				}
				sendEmails([emailToSend]);
			} else {
				const emailToSend = {
					subject: removeAiTags(
						replaceVariables(
							emailSubjectForReply?.subject
								? emailSubjectForReply.subject
								: subject
						)
					),
					body: normalizeHTML(removeAiTags(replaceVariables(body))),
					businessId: activeBusiness?._id || activeBusiness.id,
					business: activeBusiness,
					name: activeBusiness?.name,
					listId: listSelected.id || listSelected._id,
					email,
					bcc,
					userAdded,
					replyMessageId: replyMessageId || '',
					templateId: emailData?.templateId,
					isAiSnippetEmail:
						selectedTemplate?.body?.includes('[ai_snippet]')
				};
				if (emailToSend.bcc == '') {
					delete emailToSend.bcc;
				}
				sendEmails([emailToSend]);
			}
		}
	};

	useEffect(() => {
		if (!isEmpty(editedEmails)) {
			checkEmailReady();
		}
	}, [editedEmails]);

	useEffect(() => {
		const newBusinessEmails: EmailSuggestion[] = [];
		if (
			activeBusiness?.email?.length &&
			!activeBusiness?.emailLowPriority &&
			!newBusinessEmails.find(
				suggestion =>
					suggestion.email ===
					String(activeBusiness?.email).toLowerCase()
			)
		) {
			newBusinessEmails.push({
				email: String(activeBusiness?.email).toLowerCase(),
				isPrimary: true
			});
		}

		if (
			activeBusiness?.facebook?.email?.length &&
			!activeBusiness?.facebook?.emailLowPriority &&
			!newBusinessEmails.find(
				suggestion =>
					suggestion.email ===
					String(activeBusiness?.facebook?.email).toLowerCase()
			)
		) {
			newBusinessEmails.push({
				email: String(activeBusiness?.facebook?.email).toLowerCase()
			});
		}

		if (
			activeBusiness?.web?.email?.length &&
			!activeBusiness?.web?.emailLowPriority &&
			!newBusinessEmails.find(
				suggestion =>
					suggestion.email ===
					String(activeBusiness?.web?.email).toLowerCase()
			)
		) {
			newBusinessEmails.push({
				email: String(activeBusiness?.web?.email).toLowerCase()
			});
		}
		if (activeBusiness?.userAddedData?.length > 0) {
			let userGenPrimary = false;
			for (let i = 0; i < activeBusiness.userAddedData.length; i++) {
				if (
					activeBusiness?.userAddedData?.[i].type === 'email' &&
					!activeBusiness?.userAddedData[i]?.emailLowPriority &&
					!newBusinessEmails.find(
						suggestion =>
							suggestion.email ===
							String(
								activeBusiness.userAddedData[i].data
							).toLowerCase()
					)
				) {
					if (!userGenPrimary) {
						userGenPrimary = true;
						for (let x = 0; x < newBusinessEmails?.length; x++) {
							if (newBusinessEmails[x].isPrimary) {
								newBusinessEmails[x].isPrimary = false;
							}
						}
						newBusinessEmails.push({
							email: String(
								activeBusiness.userAddedData[i].data
							).toLowerCase(),
							isUserGenerated: true,
							isPrimary: true
						});
					} else {
						newBusinessEmails.push({
							email: String(
								activeBusiness.userAddedData[i].data
							).toLowerCase(),
							isUserGenerated: true
						});
					}
				}
			}
		}

		setBusinessEmails(newBusinessEmails);

		const pullFirstUserAddedEmail = () => {
			for (let i = 0; i < activeBusiness?.userAddedData?.length; i++) {
				if (
					activeBusiness.userAddedData[i].type === 'email' &&
					!activeBusiness.userAddedData[i].emailLowPriority
				) {
					return activeBusiness.userAddedData[i].data;
				}
			}
			return '';
		};

		setEmail(
			directEmail?.email ||
				editedEmails[activeBusiness?._id as keyof Email]?.email ||
				activeBusiness?.email ||
				activeBusiness?.facebook?.email ||
				activeBusiness?.web?.email ||
				pullFirstUserAddedEmail()
		);

		if (activeBusiness) {
			if (
				editedEmails[activeBusiness?._id as keyof Email]?.bcc?.length >
				0
			) {
				setBcc(editedEmails[activeBusiness?._id as keyof Email]?.bcc);
			}

			setSubject(
				editedEmails[activeBusiness?._id as keyof Email]?.subject ??
					emailData.subject
			);

			if (
				aiText?.[`${listSelected.id}`]?.[`${selectedTemplate?.id}`]?.[
					`${activeBusiness?._id}`
				] &&
				selectedTemplate.body?.includes('[ai_snippet]')
			) {
				setBody(
					editedEmails[activeBusiness?._id as keyof Email]?.body ??
						aiText[`${listSelected.id}`][`${selectedTemplate?.id}`][
							`${activeBusiness?._id}`
						].value
				);
			} else {
				setBody(
					editedEmails[activeBusiness?._id as keyof Email]?.body ??
						emailData.body
				);
			}
		}
	}, [activeBusiness, emailData]);

	const emailOnChange = (
		newSubject: string,
		newBody: string,
		newEmail: string,
		newBcc?: string
	) => {
		if (subject !== newSubject) {
			setSubject(newSubject);
		}

		if (bcc !== newBcc && bcc?.length > 0) {
			setBcc(newBcc);
		}

		if (body !== newBody) {
			setBody(newBody);
		}

		if (newEmail !== undefined && email !== newEmail) {
			setEmail(newEmail);
		}

		const checkSoftBounced = () => {
			if (
				(newEmail === activeBusiness?.email &&
					activeBusiness?.emailLowPriority) ||
				(newEmail === activeBusiness?.facebook?.email &&
					activeBusiness?.facebook?.emailLowPriority) ||
				(newEmail === activeBusiness?.web?.email &&
					activeBusiness?.web?.emailLowPriority) ||
				activeBusiness?.userAddedData?.some(data => {
					if (data?.type !== 'email') {
						return false;
					}

					if (data?.data === newEmail && data?.emailLowPriority) {
						return true;
					}

					return false;
				})
			) {
				return true;
			}

			return false;
		};

		if (
			checkSoftBounced() &&
			!isEmpty(businessEmails) &&
			!newBody.includes('[ai_snippet]')
		) {
			setEditedEmails({
				...editedEmails,
				[activeBusiness?._id ?? 0]: {
					email: businessEmails[0].email,
					subject: newSubject,
					bcc: newBcc,
					body: newBody
				}
			});

			return;
		}

		if (!newBody.includes('[ai_snippet]')) {
			setEditedEmails({
				...editedEmails,
				[activeBusiness?._id ?? 0]: {
					email: newEmail,
					subject: newSubject,
					bcc: newBcc,
					body: newBody
				}
			});
		}
	};

	const { page, totalPages, onGotoPage, onNextPage, onPreviousPage } =
		usePagination({
			skip: appliedFilters?.skip || 0,
			count: businessesCount,
			limitPerPage: appliedFilters?.limit || 50,
			onPageChange: newSkip => {
				onFilterChange({
					limit: 50,
					...appliedFilters,
					skip: newSkip
				});
			}
		});

	const toggleScheduleLink = () => {
		const button = document.getElementsByClassName(
			'add-schedule-link-dropdown-holder'
		)[0];
		if (button && !button.classList.contains('schedule-menu-open')) {
			button.classList.add('schedule-menu-open');
			setTimeout(() => {
				button.classList.add('schedule-menu-visible');
			});
		} else {
			button.classList.remove('schedule-menu-visible');
			setTimeout(() => {
				button.classList.remove('schedule-menu-open');
			}, 300);
		}
	};

	const addScheduleToEmail = type => {
		let bodyText = body || emailData.body;
		bodyText = `${bodyText}<div><br/><br/> Schedule a ${type} minute meeting with me at: https://${user.scheduleLink[type]}</div>`;
		setBody(bodyText);
		toggleScheduleLink();
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: `User added a schedule link to a reply email`,
			properties: {
				text: bodyText,
				meetingLength: type
			}
		});
	};

	const showScheduledEmailsLeft = () => {
		if (!selectedDateAndTime) {
			return user.emailLimitLeft;
		}

		if (
			!scheduledEmailLimits[scheduledEmailDate] &&
			scheduledEmailDate !== dayjs().format('MM-DD-YYYY')
		) {
			return user.emailLimit;
		}

		if (scheduledEmailDate == dayjs().format('MM-DD-YYYY')) {
			return user.emailLimitLeft;
		}

		return scheduledEmailLimits[scheduledEmailDate]?.limit;
	};

	const emailModalScrollRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (aiRegenerateSuccessId) {
			setEditedEmails({
				...editedEmails,
				[activeBusiness?._id ?? 0]: {
					...editedEmails[`${activeBusiness?._id}`],
					body: aiText[`${listSelected.id}`][
						`${selectedTemplate?.id}`
					][`${activeBusiness?._id}`].value
				}
			});
			setBody(
				aiText?.[`${listSelected.id}`]?.[`${selectedTemplate?.id}`]?.[
					`${activeBusiness?._id}`
				].value
			);
			clearAIRegenerate();
			return;
		}

		setEditedEmails({});

		if (!isEmpty(selectedTemplate)) {
			setSubject(selectedTemplate.subject);

			if (
				aiText?.[`${listSelected.id}`]?.[`${selectedTemplate?.id}`]?.[
					`${activeBusiness?._id}`
				] &&
				selectedTemplate?.body?.includes('[ai_snippet]')
			) {
				setBody(
					aiText[`${listSelected.id}`][`${selectedTemplate?.id}`][
						`${activeBusiness?._id}`
					].value
				);
			} else {
				setBody(selectedTemplate.body);
			}
		}
	}, [selectedTemplate?.id, aiRegenerateSuccessId]);

	useEffect(() => {
		if (businesses[0] && !directEmail?.email) {
			selectBusiness(businesses[0]);
		}
	}, [businesses?.[0]?._id]);

	return (
		<>
			{emailFailure &&
				String(emailFailure).trim() ===
					'Could not verify access credential.' &&
				!nylasSynced && (
					<div className="email-failure-redirect">
						<div className="email-failure-reauthenticate">
							<div className="reauthenticate-holder">
								<div className="reauthenticate-header">
									We need to re-authenticate your Email to
									access communication features
								</div>
								<div className="reauthenticate-text">
									This can be caused by email account password
									or other company email settings being
									changed. If you recently verified your
									email, please wait 10 minutes before
									re-verifying.
								</div>
								<div
									className="signup-button"
									onClick={() => {
										startEmailSignup({ user });
									}}
									onKeyDown={() => {
										startEmailSignup({ user });
									}}
								>
									<GoogleButtonIcon className="email-logo email-logo-a" />
									<MsButtonIcon className="email-logo email-logo-b" />
									<EmailSecondary className="email-logo email-logo-c" />
									<span className="button-text">
										Verify Email
									</span>
								</div>
								<span
									onClick={() => closeReauthenticate()}
									className="reauthenticate-remind-me-later"
								>
									Remind me later
								</span>
							</div>
						</div>
					</div>
				)}

			<div className="emailList relative flex h-full flex-col pt-4">
				<div className="emailList-wrapper relative flex flex-grow overflow-hidden rounded border border-solid">
					{businessesIsLoading ? (
						<div className="email-loader">
							<Loader className="flex h-full w-full items-center justify-center" />
						</div>
					) : (
						false
					)}

					{!directEmail && (
						<div
							ref={emailModalScrollRef}
							className="emailModal-scroll business-email-list w-1/2 cursor-pointer overflow-y-auto"
							id="compose-email-business-list"
						>
							{data.map((business, index) => (
								<EmailListItem
									key={uid(business._id)}
									onSelect={e => {
										setEmailStatus(Status.NONE);
										onSelect(e);

										if (
											business._id !==
											selectedBusiness?._id
										) {
											selectBusiness(business);
										}
									}}
									onHide={onHide}
									business={business}
									isActive={
										activeBusiness?._id === business._id
									}
									index={index}
									businesses={data}
									emailModalScrollRef={emailModalScrollRef}
									selectedTemplate={selectedTemplate}
								/>
							))}
						</div>
					)}

					<BusinessPreviewEmailModal
						user={user}
						setDeleteDataIndex={setDeleteDataIndex}
						to={to}
						setTo={setTo}
						business={activeBusiness}
						editedEmails={editedEmails}
						setEditedEmails={setEditedEmails}
					/>

					<Resizable
						defaultSize={{
							width: '479px',
							height: 'auto'
						}}
						maxWidth={directEmail ? '100%' : '479px'}
						minWidth="479px"
						handleWrapperClass="emailList-resizable--wrapper absolute h-full flex justify-center"
						handleComponent={{
							left: (
								<div className="emailList-drag z-150 h-full" />
							)
						}}
						enable={{
							left: directEmail === undefined
						}}
						className="full-email-modal flex flex-grow"
					>
						<div className="emailList-form flex h-full w-full flex-grow flex-col p-5">
							<EmailForm
								disabled={isSendDisabled}
								emailTo={directEmail?.email || email}
								{...setPrimary(businessEmails)}
								emailToSuggestions={businessEmails}
								emailSubject={subject || emailData.subject}
								emailBody={body || emailData.body}
								emailBcc={bcc}
								forEditing={false}
								onChange={emailOnChange}
								size="md"
								templateWarning={templateWarningOpen}
								directEmail={directEmail}
								activeBusiness={activeBusiness}
								selectedTemplate={selectedTemplate}
								setSelectedTemplate={setSelectedTemplate}
								to={to}
								setTo={setTo}
								validation={{
									email: !emailReady?.email
										? 'Invalid email provided.'
										: '',
									bcc: !emailReady?.bcc
										? 'Invalid email provided.'
										: '',
									subject: !emailReady?.subject
										? 'Subject can not be empty.'
										: '',
									body: !emailReady?.body
										? 'Body can not be empty.'
										: ''
								}}
								selectedDateAndTime={selectedDateAndTime}
								emailStatus={emailStatus}
								setShowAiLoadingModal={setShowAiLoadingModal}
								rotateTips={rotateTips}
								availableBusinesses={data}
							/>
						</div>
					</Resizable>
				</div>

				<div className="send-email-button-holder-with-loader">
					{!directEmail && totalPages > 1 && (
						<div className="pagination-holder">
							<Pagination
								currentPage={page}
								totalPages={totalPages}
								onNextPage={onNextPage}
								onPreviousPage={onPreviousPage}
								onGotoPage={onGotoPage}
							/>
						</div>
					)}

					{user &&
						user.features?.scheduler &&
						user.scheduleLink &&
						Object.keys(user.scheduleLink).length > 0 &&
						emailSubjectForReply && (
							<div
								className="add-schedule-link-button"
								onClick={toggleScheduleLink}
							>
								<GoldCalendar className="add-schedule-link-icon" />
								Add a Meeting Link
								<div
									className="add-schedule-link-dropdown-holder"
									onClick={evt => {
										evt.stopPropagation();
									}}
								>
									<div
										className="add-schedule-link-dropdown-bg"
										onClick={toggleScheduleLink}
									/>
									<div className="add-schedule-link-dropdown-inner">
										<div
											onClick={e => {
												e.stopPropagation();
												addScheduleToEmail(15);
											}}
											className="add-schedule-link-dropdown-item"
										>
											<span>15 Minutes</span>
										</div>
										<div
											onClick={e => {
												e.stopPropagation();
												addScheduleToEmail(30);
											}}
											className="add-schedule-link-dropdown-item"
										>
											<span>30 Minutes</span>
										</div>
										<div
											onClick={e => {
												e.stopPropagation();
												addScheduleToEmail(45);
											}}
											className="add-schedule-link-dropdown-item"
										>
											<span>45 Minutes</span>
										</div>
									</div>
								</div>
							</div>
						)}

					<div className="scheduled-send-holder">
						<img
							src={QuestionMarkPNG}
							className="scheduled-send-question-mark"
						/>
						<span className="scheduled-send-hovertext">
							Select a date and time to schedule your emails to
							send later. To maximize engagement, our research
							shows the best times to outreach are between{' '}
							<strong>12pm and 3pm.</strong>
						</span>
						{showEmailLimitWarning && (
							<span className="scheduled-send-timeslot-limit">
								You have reached the email limit for{' '}
								{selectedDateAndTime
									?.subtract(30, 'minute')
									.format('hh:mm A')}
							</span>
						)}
						{((scheduledEmailLimits[scheduledEmailDate]?.limit ===
							0 &&
							!scheduledEmailActivity[
								`${activeBusiness?._id}`
							]) ||
							(user.emailLimitLeft === 0 &&
								user.role !== UserRole.PreTrial &&
								!selectedDateAndTime)) && (
							<span className="scheduled-send-daily-limit">
								You have reached the daily email limit on this
								day, please pick a different date
							</span>
						)}
						<DatePickerScheduledSend
							selectedDateAndTime={selectedDateAndTime}
							setSelectedDateAndTime={setSelectedDateAndTime}
							activeBusiness={activeBusiness}
							selectedTemplate={selectedTemplate}
							hasSentEmail={emailSent[activeBusiness?._id ?? 0]}
						/>
						{user.role !== UserRole.PreTrial && (
							<span className="scheduled-send-right-holder">
								<span className="scheduled-send-right-inner-holder">
									<ChatGPT className="ai-credits-left-icon" />
									<span
										className={`ai-credits-left-count ${aiCredits <= 0 ? 'red' : ''} `}
									>
										{aiCredits}
									</span>
									&nbsp;left
									<EnvelopeRounded className="scheduled-send-emails-left-envelope" />
									<span
										className={`scheduled-send-emails-left-count ${scheduledEmailLimits[scheduledEmailDate]?.units[scheduledEmailDateAndTime]?.limit === 0 ? 'green' : ''} ${scheduledEmailLimits[scheduledEmailDate]?.limit === 0 ? 'red' : ''} `}
									>
										{showScheduledEmailsLeft()}
									</span>
									&nbsp;left
								</span>
							</span>
						)}
					</div>

					<button
						id="email-deliver-button"
						className={classNames(
							'send-email-button',
							{
								loading: emailStatus === Status.LOADING
							},
							{
								'emailList-send-disabled cursor-not-allowed border border-transparent font-semibold':
									disabledClassCheck(),
								primary: !disabledClassCheck()
							}
						)}
						type="button"
						onClick={sendEmail}
						disabled={disabledClassCheck()}
					>
						{emailStatus === Status.LOADING
							? ''
							: selectedDateAndTime
								? 'Schedule email'
								: 'Send email'}
						{emailStatus === Status.SUCCESS && (
							<Loader
								className="send-email-button-loader-success"
								status={Status.SUCCESS}
							/>
						)}
						{emailStatus === Status.FAILURE && (
							<>
								<span className="send-email-button-failure-text">
									Send failed. Try again.
								</span>
								<Loader
									className="send-email-button-loader-failure"
									status={Status.FAILURE}
								/>
							</>
						)}
						{emailStatus === Status.LOADING && (
							<Loader
								className="send-email-button-loader-loading"
								status={Status.LOADING}
							/>
						)}
					</button>

					{nylasSynced == false && (
						<div className="nylas-sync-hovertext-holder">
							<span className="nylas-sync-hovertext">
								You are currently disconnected from Nylas,
								please re-verify your email address to continue
								sending emails. If you recently verified, please
								wait 10 minutes before attempting to reverify.
							</span>
						</div>
					)}
				</div>
			</div>
		</>
	);
}
